// W47b (2026-10-21): the approved gift catalog card (data-component
// "gift-catalog", 07-Gifts-Consistency-Review-v5.html READ) rendered from
// the W47 catalog: 18 items, names only. Every tile shows "Price from the
// pricing engine" where the design shows a sample price / stock line; no
// price is typed and stock reads "Stock not read" while the api-server
// reports stock 'unknown'.
import type { ReactNode } from 'react';
import {
  GIFT_CATEGORY_TABS,
  GIFT_PRICE_PLACEHOLDER,
  GIFT_STOCK_NOT_READ,
  catalogByCategory,
  monogramFor,
  toneFor,
  type GiftCategoryCode,
  type LiveGiftCatalogItem,
} from '../liveModel.js';

export interface LiveGiftCatalogProps {
  catalog: LiveGiftCatalogItem[];
  category: GiftCategoryCode;
  selectedId: string | null;
  /** Head pill: "Eligibility unknown" / "Not eligible" / "Eligible". */
  statusLabel: string;
  eligible: boolean | null;
  onCategoryChange: (key: GiftCategoryCode) => void;
  onSelect: (item: LiveGiftCatalogItem) => void;
}

function GiftArt({ item }: { item: LiveGiftCatalogItem }): ReactNode {
  const tone = toneFor(item);
  if (item.category === 't_shirt') {
    return (
      <span className={`gift-art ${tone}`}>
        <svg viewBox="0 0 48 48"><path d="m16 8-11 7 6 10 6-3v19h14V22l6 3 6-10-11-7c-2 6-14 6-16 0Z" /></svg>
      </span>
    );
  }
  if (item.category === 'ball_cap') {
    return (
      <span className={`gift-art ${tone}`}>
        <svg viewBox="0 0 48 48"><path d="M8 28v-5a16 16 0 0 1 32 0v5H8Zm0 0L3 34h25l12-6M25 8v20" /></svg>
      </span>
    );
  }
  return (
    <span className={`gift-art ${tone}`}>
      <span className="product-monogram">{monogramFor(item.name)}</span>
    </span>
  );
}

export function LiveGiftCatalog({ catalog, category, selectedId, statusLabel, eligible, onCategoryChange, onSelect }: LiveGiftCatalogProps): ReactNode {
  const tab = GIFT_CATEGORY_TABS.find(t => t.key === category) ?? GIFT_CATEGORY_TABS[0]!;
  const items = catalogByCategory(catalog, category);
  return (
    <section className="card" data-component="gift-catalog" data-testid="gifts-live-catalog">
      <div className="card-head">
        <div className="row">
          <span className="head-icon"><svg><use href="#box" /></svg></span>
          <h3>Choose a gift</h3>
        </div>
        <span className={eligible === true ? 'active-pill' : 'gift-status-pill'} data-testid="gifts-catalog-status">{statusLabel}</span>
      </div>
      <div className="gift-category-tabs" role="group" aria-label="Gift category">
        {GIFT_CATEGORY_TABS.map(t => (
          <button key={t.key} type="button" aria-pressed={category === t.key} onClick={() => onCategoryChange(t.key)}>{t.label}</button>
        ))}
      </div>
      <div className="gift-category-heading">
        <div>
          <h3 id="gift-category-title">{tab.title}</h3>
          <p id="gift-category-description">{tab.description}</p>
        </div>
        <span>{catalog.length} items · names only</span>
      </div>
      <div className="gift-catalog" id="variant-catalog">
        {items.length === 0 ? (
          <p className="gift-catalog-note" data-testid="gifts-catalog-empty">No items in this category on the catalog read.</p>
        ) : items.map(item => {
          const picked = item.id === selectedId;
          return (
            <button key={item.id} type="button" className={picked ? 'gift-tile picked' : 'gift-tile'} aria-pressed={picked} onClick={() => onSelect(item)} data-testid="gifts-catalog-tile">
              <GiftArt item={item} />
              <span className="gift-kind">{item.kind}</span>
              <strong>{item.name}</strong>
              <small>{item.variant ?? ''}</small>
              {/* price is null from this source; a number would still never be typed here */}
              <span className="stock-unknown" data-testid="gifts-tile-price">{GIFT_PRICE_PLACEHOLDER}</span>
              <span className="stock-unknown">{item.stock === 'unknown' || item.stock === null ? GIFT_STOCK_NOT_READ : `Stock: ${item.stock}`}</span>
            </button>
          );
        })}
      </div>
      <p className="gift-catalog-note">
        Catalog names come from the approved gift list. Price, variant and stock are bound by the pricing engine and the gift program; none are read on this deployment.
      </p>
    </section>
  );
}
